/**
 * Combo (bar + line) generator: buckets the prepared days, derives the bar and line metrics,
 * and computes two independent Y scales (bars → left axis, line → right axis) that share the
 * same number of intervals so their grid lines line up. Pure geometry, no SVG strings, so the
 * web preview can reuse it for hover tooltips.
 */
import { GraphOptions } from '../options';
import {
    Bucket,
    Bucketing,
    METRICS,
    bucketDays,
    bucketLabel,
    bucketTitle,
    chooseBucketing,
    formatCompact,
    metricValues,
    resolveMetric,
} from '../shared/aggregate';
import { buildCurve } from '../shared/curve';
import { alignedScale } from '../shared/scale';
import { estimateTextWidth } from '../shared/svg';
import { PreparedData } from '../shared/types';

export const DEFAULT_COMBO_WIDTH = 900;
export const DEFAULT_COMBO_HEIGHT = 400;
/** Both axes use this many intervals so their gridlines coincide. */
const INTERVALS = 4;

export interface ComboItem {
    bucket: Bucket;
    /** Center of the bucket's slot. */
    x: number;
    barValue: number;
    lineValue: number;
    bar: { x: number; y: number; width: number; height: number };
    lineY: number;
    /** e.g. `September 2026` */
    title: string;
}

export interface AxisTick {
    y: number;
    label: string;
}

export interface LegendEntry {
    kind: 'bar' | 'line';
    label: string;
    x: number;
}

export interface ComboLayout {
    width: number;
    height: number;
    plot: { left: number; right: number; top: number; bottom: number };
    slot: number;
    bucketing: Bucketing;
    showBars: boolean;
    showLine: boolean;
    barLabel: string;
    lineLabel: string;
    title: { text: string; x: number; y: number; size: number } | null;
    total: { text: string; x: number; y: number } | null;
    legend: { y: number; entries: LegendEntry[] } | null;
    /** Gridline positions (shared by both axes). */
    gridY: number[];
    leftTicks: AxisTick[];
    rightTicks: AxisTick[];
    xLabels: Array<{ x: number; text: string }>;
    items: ComboItem[];
    linePath: string;
    pointRadius: number;
}

const SWATCH = 12;

const layoutLegend = (
    labels: Array<{ kind: 'bar' | 'line'; label: string }>,
    width: number,
    fontSize: number,
): LegendEntry[] => {
    const itemWidths = labels.map((l) => SWATCH + 6 + estimateTextWidth(l.label, fontSize));
    const gap = fontSize * 1.8;
    const total = itemWidths.reduce((a, b) => a + b, 0) + gap * (labels.length - 1);
    let x = (width - total) / 2;
    return labels.map((l, i) => {
        const entry = { ...l, x };
        x += itemWidths[i] + gap;
        return entry;
    });
};

export const layoutCombo = (data: PreparedData, options: GraphOptions): ComboLayout => {
    const width = options.width || DEFAULT_COMBO_WIDTH;
    const height = options.height || DEFAULT_COMBO_HEIGHT;
    const fs = options.fontSize;
    const pad = options.padding;
    const titleSize = Math.round(fs * 1.5);

    const showBars = options.series !== 'line';
    const showLine = options.series !== 'bars';
    const bucketing =
        options.groupBy === 'auto' ? chooseBucketing(data.days.length) : options.groupBy;
    const buckets = bucketDays(data.days, bucketing);
    const barMetric = resolveMetric(options.barMetric, 'bar', bucketing);
    const lineMetric = resolveMetric(options.lineMetric, 'line', bucketing);
    const barValues = metricValues(buckets, barMetric);
    const lineValues = metricValues(buckets, lineMetric);
    const leftScale = alignedScale(Math.max(0, ...barValues), INTERVALS);
    const rightScale = alignedScale(Math.max(0, ...lineValues), INTERVALS);
    const barLabel = METRICS[barMetric].label;
    const lineLabel = METRICS[lineMetric].label;

    // Header
    let cursor = pad;
    let title: ComboLayout['title'] = null;
    let total: ComboLayout['total'] = null;
    if (options.showTitle) {
        cursor += titleSize;
        title = {
            text: options.title || `${data.name?.trim() || data.username}'s Contribution Activity`,
            x: width / 2,
            y: cursor,
            size: titleSize,
        };
        cursor += Math.round(fs * 0.6);
    }
    if (options.showTotal) {
        cursor += Math.round(fs * 1.3);
        total = { text: data.totalText, x: width / 2, y: cursor };
    }
    if (title || total) cursor += Math.round(fs * 1.2);

    // Legend
    const legendLabels = [
        ...(showBars ? [{ kind: 'bar' as const, label: barLabel }] : []),
        ...(showLine ? [{ kind: 'line' as const, label: lineLabel }] : []),
    ];
    const legendHeight = Math.round(fs * 2.2);
    let legend: ComboLayout['legend'] = null;
    if (options.showLegend && options.legendPosition === 'top') {
        legend = { y: cursor + fs * 0.5, entries: layoutLegend(legendLabels, width, fs) };
        cursor += legendHeight;
    }

    // Axes
    const showLeft = options.showLeftAxis && showBars;
    const showRight = options.showRightAxis && showLine;
    const axisWidth = (max: number) => Math.round(estimateTextWidth(formatCompact(max), fs) + fs);
    const markerRadius =
        showLine && options.showPoints ? options.pointRadius + options.pointBorderWidth : 0;
    const xLabelHeight = options.showXLabels ? Math.round(fs * 2) : 0;
    const bottomLegend = options.showLegend && options.legendPosition === 'bottom';

    const plot = {
        left: pad + (showLeft ? axisWidth(leftScale.max) : 0),
        right: width - pad - (showRight ? axisWidth(rightScale.max) : 0),
        top: cursor + Math.max(markerRadius, showLine ? options.lineWidth : 0) + 6,
        bottom: height - pad - xLabelHeight - (bottomLegend ? legendHeight : 0),
    };
    if (plot.right - plot.left < 20) plot.right = plot.left + 20;
    if (plot.bottom - plot.top < 20) plot.bottom = plot.top + 20;
    const plotWidth = plot.right - plot.left;
    const plotHeight = plot.bottom - plot.top;

    if (bottomLegend)
        legend = {
            y: plot.bottom + xLabelHeight + fs * 0.9,
            entries: layoutLegend(legendLabels, width, fs),
        };

    const n = buckets.length;
    const slot = n ? plotWidth / n : plotWidth;
    const barWidth = Math.max(1, (slot * options.barWidth) / 100);
    const yFor = (value: number, max: number) => plot.bottom - (value / max) * plotHeight;

    const items: ComboItem[] = buckets.map((bucket, i) => {
        const x = plot.left + slot * (i + 0.5);
        const barTop = yFor(barValues[i], leftScale.max);
        return {
            bucket,
            x,
            barValue: barValues[i],
            lineValue: lineValues[i],
            bar: { x: x - barWidth / 2, y: barTop, width: barWidth, height: plot.bottom - barTop },
            lineY: yFor(lineValues[i], rightScale.max),
            title: bucketTitle(bucket, bucketing),
        };
    });

    const gridY = Array.from(
        { length: INTERVALS + 1 },
        (_, k) => plot.bottom - (k / INTERVALS) * plotHeight,
    );
    const ticks = (scale: { step: number }): AxisTick[] =>
        gridY.map((y, k) => ({ y, label: formatCompact(k * scale.step) }));

    // X labels: every k-th bucket, anchored on the latest one.
    const xLabels: ComboLayout['xLabels'] = [];
    if (n && (options.showXLabels || options.showGridX)) {
        const maxLabels = Math.max(2, Math.floor(plotWidth / (fs * 4.2)));
        const every = Math.ceil(n / maxLabels);
        const spansYears = buckets[0].start.slice(0, 4) !== buckets[n - 1].start.slice(0, 4);
        let previous: Bucket | undefined;
        items.forEach((item, i) => {
            if ((n - 1 - i) % every !== 0) return;
            xLabels.push({
                x: item.x,
                text: bucketLabel(item.bucket, bucketing, previous, spansYears),
            });
            previous = item.bucket;
        });
    }

    const pointRadius =
        showLine && options.showPoints ? Math.min(options.pointRadius, slot * 0.4) : 0;

    return {
        width,
        height,
        plot,
        slot,
        bucketing,
        showBars,
        showLine,
        barLabel,
        lineLabel,
        title,
        total,
        legend,
        gridY,
        leftTicks: showLeft ? ticks(leftScale) : [],
        rightTicks: showRight ? ticks(rightScale) : [],
        xLabels,
        items,
        linePath: showLine
            ? buildCurve(
                  items.map((item) => ({ x: item.x, y: item.lineY })),
                  options.smoothing / 100,
              )
            : '',
        pointRadius: pointRadius >= 1.5 ? pointRadius : 0,
    };
};
